import React, { useEffect, useState } from 'react';
import { Download, Monitor, Smartphone, X } from 'lucide-react';

// Helper function to detect iOS Safari
const isIOSSafari = (): boolean => {
  if (typeof window === 'undefined') return false;
  const userAgent = window.navigator.userAgent.toLowerCase();
  const isIOS = /iphone|ipad|ipod/.test(userAgent);
  const isSafari = /safari/.test(userAgent) && !/chrome|crios|fxios/.test(userAgent);
  return isIOS && isSafari;
};

// Helper function to detect platform
const getPlatformInstructions = (): string => {
  if (typeof window === 'undefined') return '';
  
  const userAgent = window.navigator.userAgent.toLowerCase();
  const isIOS = /iphone|ipad|ipod/.test(userAgent);
  const isAndroid = /android/.test(userAgent);
  const isMac = /macintosh|mac os x/.test(userAgent);
  const isWindows = /windows/.test(userAgent);
  const isChrome = /chrome/.test(userAgent) && !/edge|edg/.test(userAgent);
  const isEdge = /edge|edg/.test(userAgent);
  const isSafari = /safari/.test(userAgent) && !/chrome|crios|fxios/.test(userAgent);
  
  if (isIOS && isSafari) {
    return 'Tap the Share button (square with arrow) at the bottom, then select "Add to Home Screen"';
  } else if (isAndroid && isChrome) {
    return 'Tap the menu (⋮) in the top right, then select "Install app" or "Add to Home screen"';
  } else if (isMac && (isChrome || isEdge || isSafari)) {
    return 'Click the install icon (⊕) in your browser\'s address bar, or the (⊕)Open in app at the address bar at the top right =>, or use the browser menu';
  } else if (isWindows && (isChrome || isEdge)) {
    return 'Click the install icon (⊕) in your browser\'s address bar, or the (⊕)Open in app at the address bar at the top right =>, or use the browser menu';
  }
  
  return 'Look for the install icon (⊕) in your browser\'s address bar, or use the browser menu to install LinguaLearn';
};

// LocalStorage keys for tracking dismissals
const DISMISS_COUNT_KEY = 'pwa-install-dismiss-count';
const COOLDOWN_UNTIL_KEY = 'pwa-install-cooldown-until';
const COOLDOWN_DAYS = 30;

// Helper functions for localStorage management
const getDismissCount = (): number => {
  if (typeof window === 'undefined') return 0;
  const count = localStorage.getItem(DISMISS_COUNT_KEY);
  return count ? parseInt(count, 10) : 0;
};

const setDismissCount = (count: number): void => {
  if (typeof window === 'undefined') return;
  localStorage.setItem(DISMISS_COUNT_KEY, count.toString());
};

const getCooldownUntil = (): number | null => {
  if (typeof window === 'undefined') return null;
  const timestamp = localStorage.getItem(COOLDOWN_UNTIL_KEY);
  return timestamp ? parseInt(timestamp, 10) : null;
};

const setCooldownUntil = (timestamp: number): void => {
  if (typeof window === 'undefined') return;
  localStorage.setItem(COOLDOWN_UNTIL_KEY, timestamp.toString());
};

const isInCooldown = (): boolean => {
  const cooldownUntil = getCooldownUntil();
  if (!cooldownUntil) return false;
  return Date.now() < cooldownUntil;
};

const shouldShowCard = (): boolean => {
  return !isInCooldown();
};

export default function PWAInstallCard() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [shouldShow, setShouldShow] = useState(true);

  useEffect(() => {
    // Check if app is already installed/running in standalone mode
    const isRunningStandalone = (): boolean => {
      if (typeof window === 'undefined') return false;
      const isStandaloneMedia =
        Boolean(window.matchMedia) &&
        (window.matchMedia('(display-mode: standalone)').matches ||
          window.matchMedia('(display-mode: minimal-ui)').matches ||
          window.matchMedia('(display-mode: window-controls-overlay)').matches ||
          window.matchMedia('(display-mode: fullscreen)').matches);
      const isIosStandalone = navigator.standalone === true;
      return Boolean(isStandaloneMedia || isIosStandalone);
    };

    const checkStandalone = () => {
      const isStandaloneMode = isRunningStandalone();
      // Check localStorage as well
      const isInstalledLocally = localStorage.getItem('pwa-installed') === 'true';

      setIsStandalone(isStandaloneMode || isInstalledLocally);
    };

    checkStandalone();
    
    // Check if we should show the card based on dismissal history
    setShouldShow(shouldShowCard());

    // Listen for window resize/media query changes to update standalone status
    let mediaQuery: MediaQueryList | null = null;
    const handleChange = () => {
      setIsStandalone(isRunningStandalone() || localStorage.getItem('pwa-installed') === 'true');
    };

    if (window.matchMedia) {
      mediaQuery = window.matchMedia('(display-mode: standalone)');
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', handleChange);
      } else {
        // Fallback for older browsers
        mediaQuery.addListener(handleChange);
      }
    }

    // Capture the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: BeforeInstallPromptEvent) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Stash the event so it can be triggered later.
      setDeferredPrompt(e);
      // Update UI notify the user they can install the PWA
    };

    const handleAppInstalled = () => {
      localStorage.setItem('pwa-installed', 'true');
      setIsStandalone(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // Also try to check using getInstalledRelatedApps if supported
    if (typeof navigator !== 'undefined' && 'getInstalledRelatedApps' in navigator && navigator.getInstalledRelatedApps) {
      navigator.getInstalledRelatedApps().then((relatedApps) => {
        if (relatedApps && relatedApps.length > 0) {
          localStorage.setItem('pwa-installed', 'true');
          setIsStandalone(true);
        }
      }).catch(console.error);
    }

    return () => {
      if (mediaQuery) {
        if (mediaQuery.removeEventListener) {
          mediaQuery.removeEventListener('change', handleChange);
        } else {
          mediaQuery.removeListener(handleChange);
        }
      }
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    // If we have the prompt, use it to trigger installation
    if (deferredPrompt) {
      try {
        // Show the install prompt
        await deferredPrompt.prompt();
        // Wait for the user to respond to the prompt
        const { outcome } = await deferredPrompt.userChoice;
        
        if (outcome === 'accepted') {
          console.log('User accepted the install prompt');
          localStorage.setItem('pwa-installed', 'true');
          setIsStandalone(true);
        } else {
          console.log('User dismissed the install prompt');
        }
        
        // We've used the prompt, and can't use it again, discard it
        setDeferredPrompt(null);
      } catch (error) {
        console.error('Error showing install prompt:', error);
        // Fallback to platform-specific instructions
        const instructions = getPlatformInstructions();
        alert(`Install LinguaLearn:\n\n${instructions}`);
      }
    } else {
      // Prompt not available - show platform-specific instructions
      // On iOS Safari and some browsers, programmatic installation isn't supported
      const instructions = getPlatformInstructions();
      alert(`Install LinguaLearn:\n\n${instructions}`);
    }
  };

  const handleDismiss = () => {
    const currentCount = getDismissCount();
    const newCount = currentCount + 1;
    
    setDismissCount(newCount);
    
    // Set cooldown for 30 days once dismissed
    const cooldownUntil = Date.now() + (COOLDOWN_DAYS * 24 * 60 * 60 * 1000);
    setCooldownUntil(cooldownUntil);
    
    setIsDismissed(true);
    setShouldShow(false);
  };

  // Do not render if:
  // 1. App is already in standalone mode or installed locally
  // 2. User has dismissed the card in this session
  // 3. Should not show based on dismissal history (cooldown period active)
  if (isStandalone || isDismissed || !shouldShow) {
    return null;
  }

  // Only show if:
  // - We have an install prompt event (browser confirms app is installable and not already installed)
  // - OR user is on iOS Safari (where beforeinstallprompt is not supported, manual Add to Home Screen instructions apply)
  const canPromptInstall = Boolean(deferredPrompt);
  const isIOS = isIOSSafari();

  if (!canPromptInstall && !isIOS) {
    return null;
  }

  return (
    <div className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl p-6 mb-6 text-white shadow-lg relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-white opacity-10 rounded-full blur-xl"></div>
      <div className="absolute bottom-0 left-0 -mb-4 -ml-4 w-20 h-20 bg-white opacity-10 rounded-full blur-xl"></div>

      <button
        onClick={handleDismiss}
        className="absolute top-3 right-3 p-1 rounded-full hover:bg-white/20 transition-colors"
        aria-label="Dismiss"
      >
        <X className="w-5 h-5" />
      </button>

      <div className="flex flex-col md:flex-row items-center gap-6 relative z-10">
        <div className="flex-shrink-0 bg-white/20 p-4 rounded-full backdrop-blur-sm">
          <Download className="w-8 h-8 md:w-10 md:h-10 text-white" />
        </div>

        <div className="flex-1 text-center md:text-left">
          <h2 className="text-xl md:text-2xl font-bold mb-2">Install LinguaLearn App</h2>
          <p className="text-purple-100 mb-4 md:mb-0 max-w-xl">
            Get the best experience! Install the app on your computer or phone for faster access and offline capabilities.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleInstallClick}
            className="flex items-center justify-center gap-2 px-6 py-3 bg-white text-purple-600 rounded-lg font-bold hover:bg-purple-50 transition-colors shadow-md"
          >
            <Download className="w-5 h-5" />
            Install App
          </button>
        </div>
      </div>

      <div className="mt-4 flex justify-center md:justify-start gap-4 text-xs text-purple-200 opacity-80">
        <span className="flex items-center gap-1">
          <Monitor className="w-3 h-3" /> Desktop
        </span>
        <span className="flex items-center gap-1">
          <Smartphone className="w-3 h-3" /> Mobile
        </span>
      </div>
    </div>
  );
}
